import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useIsModerator } from '@/hooks/useUserRole';

export interface NotificationWithActor {
  id: string;
  user_id: string;
  type: string;
  title: string;
  body: string | null;
  actor_id: string | null;
  related_post_id: string | null;
  related_comment_id: string | null;
  related_matchup_id: string | null;
  is_read: boolean;
  created_at: string;
  actor_profile: {
    full_name: string;
    avatar_url: string | null;
    company: string | null;
    role: string | null;
  } | null;
}

export function useNotifications() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async (): Promise<NotificationWithActor[]> => {
      if (!user?.id) return [];

      const { data: notifications, error } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      if (!notifications || notifications.length === 0) return [];

      // Get unique actor IDs
      const actorIds = [...new Set(notifications.map(n => n.actor_id).filter(Boolean))] as string[];

      let profileMap = new Map<string, { full_name: string; avatar_url: string | null; company: string | null; role: string | null }>();

      if (actorIds.length > 0) {
        const { data: profiles } = await supabase
          .from('profiles')
          .select('user_id, full_name, avatar_url, company, role')
          .in('user_id', actorIds);

        if (profiles) {
          profileMap = new Map(profiles.map(p => [p.user_id, p]));
        }
      }

      return notifications.map(n => ({
        ...n,
        actor_profile: n.actor_id ? profileMap.get(n.actor_id) || null : null,
      }));
    },
    enabled: !!user?.id,
  });
}

export function useUnreadCount() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['notifications-unread-count', user?.id],
    queryFn: async (): Promise<number> => {
      if (!user?.id) return 0;

      const { count, error } = await supabase
        .from('notifications')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!user?.id,
    refetchInterval: 30000,
  });
}

export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('id', notificationId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

export function useMarkAllAsRead() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('notifications')
        .update({ is_read: true })
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['notifications-unread-count'] });
    },
  });
}

// Regex to extract user mentions from content: @[user_id:display_name]
const MENTION_REGEX = /@\[([a-f0-9-]+):([^\]]+)\]/g;
const EVERYONE_MENTION = /@everyone/;

export function useCreateMentionNotifications() {
  const { user } = useAuth();
  const { isModerator } = useIsModerator();

  return useMutation({
    mutationFn: async ({
      content,
      postId,
      commentId,
    }: {
      content: string;
      postId?: string;
      commentId?: string;
    }) => {
      if (!user?.id) return;

      const mentionedUserIds: string[] = [];

      // Check for @everyone
      const isEveryoneMentioned = EVERYONE_MENTION.test(content);

      // Extract individual user mentions
      let match;
      while ((match = MENTION_REGEX.exec(content)) !== null) {
        const userId = match[1];
        if (userId !== user.id && !mentionedUserIds.includes(userId)) {
          mentionedUserIds.push(userId);
        }
      }

      // If @everyone, only allow for moderators/admins to avoid abuse
      if (isEveryoneMentioned && isModerator) {
        const { data: allProfiles } = await supabase
          .from('profiles')
          .select('user_id')
          .neq('user_id', user.id);

        if (allProfiles) {
          for (const profile of allProfiles) {
            if (!mentionedUserIds.includes(profile.user_id)) {
              mentionedUserIds.push(profile.user_id);
            }
          }
        }
      }

      if (mentionedUserIds.length === 0) return;

      // Use SECURITY DEFINER function (server-side caps at 200)
      const { error } = await supabase.rpc('create_mention_notifications', {
        p_actor_id: user.id,
        p_mentioned_user_ids: mentionedUserIds.slice(0, 200),
        p_title: 'mentioned you',
        p_body: content.substring(0, 100),
        p_related_post_id: postId || null,
        p_related_comment_id: commentId || null,
      });

      if (error) {
        console.error('Failed to create mention notifications:', error);
      }
    },
  });
}
